import classnames from "classnames";
import * as React from "react";

import { getDurationFromCSSvar } from "seneca-common/utils/functions";
import isValidFunction from "seneca-common/utils/functions/checks/isValidFunction";

import Fade from "features/common/components/FadeIn";
import OpenCloseStateContainer from "features/common/components/OpenCloseStateContainer";
import { CloseIcon, Color } from "features/common/icons";
import { ModalPortal } from "features/common/modals/components/ModalNodeContext";
import {
  InjectedProps as MakeModalContainerProps,
  makeModalContainer
} from "features/common/modals/containers/Modal";

import styles from "./SideDrawer.module.scss";

const ANIMATION_DURATION = getDurationFromCSSvar(styles.animationduration);

type OpenCloseStateContainerProps = {
  open: boolean;
  setOpen: () => void;
  setClosed: () => void;
};

export type Props = {
  openFromRight?: boolean;
  children?: React.ReactNode;
  renderContents?: (arg0: () => void, arg1: () => void) => React.ReactNode;
  closeColor?: Color | null | undefined;
  sideDrawerClassName?: string;
  renderButton: (onClick: () => void) => React.ReactNode;
};

type ComponentProps = MakeModalContainerProps &
  OpenCloseStateContainerProps &
  Props;

type State = {
  mountDrawer: boolean;
};

class SideDrawer extends React.PureComponent<ComponentProps, State> {
  unmountTimeout: NodeJS.Timeout | undefined;
  setOpenAfterMountedTimeout: NodeJS.Timeout | undefined;

  state = {
    mountDrawer: false
  };

  componentWillUnmount() {
    const { setModalClosed } = this.props;
    setModalClosed();
    clearTimeout(this.unmountTimeout!);
    clearTimeout(this.setOpenAfterMountedTimeout!);
  }

  openDrawer = () => {
    const { setModalOpen } = this.props;

    if (isValidFunction(setModalOpen)) {
      setModalOpen();
    }

    // If we mount the drawer and set open = true in the same process.tick the
    // drawer will mount in the open position (i.e. not animate in). Therefore we
    // have to mount the drawer first then set it open in the next process.tick
    clearTimeout(this.unmountTimeout!);
    this.setState({
      mountDrawer: true
    });
    this.setOpenAfterMountedTimeout = setTimeout(() => this.props.setOpen(), 0);
  };

  closeDrawer = () => {
    const { setModalClosed } = this.props;

    if (isValidFunction(setModalClosed)) {
      setModalClosed();
    }

    this.unmountDrawerAfterAnimateOut();
    this.props.setClosed();
  };

  unmountDrawerAfterAnimateOut = () => {
    this.unmountTimeout = setTimeout(
      () =>
        this.setState({
          mountDrawer: false
        }),
      ANIMATION_DURATION
    );
  };

  render() {
    const { mountDrawer } = this.state;
    const { open, renderButton } = this.props;
    const onClick = open ? this.closeDrawer : this.openDrawer;

    return (
      <>
        {renderButton(onClick)}
        {mountDrawer && this.renderDrawer()}
      </>
    );
  }

  renderDrawer() {
    const { children, renderContents, open, sideDrawerClassName, closeColor } =
      this.props;
    return (
      <ModalPortal>
        <Fade show={open}>
          <div className={styles.overlay} onClick={this.closeDrawer} />
        </Fade>
        <div
          className={classnames(
            sideDrawerClassName ? sideDrawerClassName : styles.drawer,
            {
              [styles.drawerLeft]: !this.props.openFromRight,
              [styles.right]: this.props.openFromRight,
              [styles.open]: open && !this.props.openFromRight,
              [styles.openRight]: open && this.props.openFromRight
            }
          )}
        >
          <div className={styles.inner} data-test="SideDrawer__inner">
            {renderContents
              ? renderContents(this.closeDrawer, this.openDrawer)
              : children && children}

            <CloseIcon
              color={closeColor}
              wrapperClassName={styles.close}
              onClick={this.closeDrawer}
            />
          </div>
        </div>
      </ModalPortal>
    );
  }
}

export default makeModalContainer(({ ...modalContainerProps }) => (
  <OpenCloseStateContainer
    renderComponent={(open, setOpen, setClosed) => {
      return (
        <SideDrawer
          open={open}
          setOpen={setOpen}
          setClosed={setClosed}
          {...modalContainerProps}
        />
      );
    }}
  />
));
